'use client'

import DropDown from './dropdown.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { useMediaQuery } from 'react-responsive'
import { useMemo, useState, useEffect } from 'react'
import { withLayout } from '../../shared/Styles.js'
import { formatVariantPrice } from '../ShelfDisplay/shelfDisplay.js'
import { Picture, Text, Button, Heading } from '../../components/index.js'
import { cn } from '../../helpers/index.js'
import React, { useImperativeHandle, useRef } from 'react'

export interface SpecsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  data?: any
  buildData?: any
  onChange?: (product: any, index: number) => void
  onSecondaryChange?: (data: any) => void
}

const Specs = React.forwardRef<HTMLDivElement, SpecsProps>(
  ({ data, buildData, onChange, onSecondaryChange, className, ...rest }, ref) => {
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' })

    const [active, setActive] = useState<any>({})
    const [menuList, setMenuList] = useState<any[]>([])
    const [isShowMax, setIsShowMax] = useState<boolean>(false)
    const [visibleActive, setVisibleActive] = useState<{ [key: number]: boolean }>({})

    const innerRef = useRef<HTMLDivElement>(null)

    const { locale = 'us', currencyDisplay } = useAiuiContext()

    const { LeftMenu, RightMenu, DefaultSelectMenu } = useMemo(() => data?.data || {}, [data?.data])

    const isImageUrl = (urlPath: string) => {
      try {
        const url = new URL(urlPath)
        const path = url.pathname.toLowerCase()
        return /\.(jpeg|jpg|gif|png|webp|bmp|svg|tiff)$/.test(path)
      } catch (e) {
        // 如果传入的不是有效URL（如相对路径），直接检查路径
        return /\.(jpeg|jpg|gif|png|webp|bmp|svg|tiff)$/i.test(urlPath)
      }
    }

    const skuNode = (product: any, index: number) => {
      const findSku = product?.variants?.find?.((item: any) => item?.sku === product?.sku)
      const coupon = findSku?.coupons?.[0]
      const { price, basePrice } = formatVariantPrice({
        locale: locale || 'us',
        amount: coupon ? coupon?.variant_price4wscode : findSku?.price,
        baseAmount: coupon ? findSku?.price : 0,
        currencyDisplay,
        currencyCode: product?.price?.currencyCode || 'USD',
      })
      const buttonTextValue =
        typeof DefaultSelectMenu?.buttonText === 'string'
          ? DefaultSelectMenu?.buttonText
          : DefaultSelectMenu?.buttonText?.[String(index)] || ''
      return (
        <div className="specs-sku-node-wrap box-border">
          <Text
            className={`md-tablet:p-2 l-tablet:mb-4 l-tablet:text-sm desktop:px-6 lg-desktop:mb-8 lg-desktop:text-lg openDropDown specs-sku-node-text relative mb-6 box-border block rounded-[50px] border border-[#E4E5E6] bg-[#F5F5F7] px-6 py-4 text-base font-bold`}
            onClick={() => {
              if (menuList?.length <= 1) return null

              setVisibleActive({
                ...visibleActive,
                [index]: !visibleActive?.[index],
              })
            }}
          >
            <div className="flex items-center justify-between overflow-hidden">
              <div
                className="openDropDown md-tablet:text-sm lg-desktop:text-lg specs-sku-node-title flex-1 truncate text-base"
                title={active?.[index]?.name || ''}
              >
                {active?.[index]?.name || ''}
              </div>
              {menuList?.length > 1 ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  className="openDropDown"
                >
                  <path
                    d="M6 9.5L12 15.5L18 9.5"
                    stroke="#1D1D1F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : null}
            </div>
            <DropDown
              index={index}
              list={menuList}
              active={active}
              setActive={setActive}
              visible={visibleActive}
              setVisible={setVisibleActive}
            />
          </Text>
          <div className="lg-desktop:max-h-[196px] lg-desktop:max-w-[196px] max-h-[138px] max-w-[138px]">
            <Picture
              source={findSku?.image?.url || ''}
              className="aspect-h-[280] aspect-w-[280]"
              imgClassName="w-full h-full object-cover hover:scale-105 transition-all duration-300"
            />
          </div>
          <div className="lg-desktop:text-2xl mb-2 mt-6 text-xl font-bold">
            <div className="lg-desktop:min-h-[32px] flex  min-h-[28px] items-center">
              {findSku?.availableForSale ? (
                <>
                  <div className="specs-sku-node-price text-[#080A0F]">{price || ''}</div>
                  {basePrice && (
                    <div className="lg-desktop:text-2xl specs-sku-node-base-price ml-1 text-xl text-[#86868C] line-through">
                      {basePrice}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
          {buttonTextValue ? (
            <Button
              onClick={() => {
                onChange?.(product, index)
              }}
              className="specs-sku-node-button"
            >
              {buttonTextValue}
            </Button>
          ) : null}
        </div>
      )
    }

    const normalNode = (v: any) => {
      if (v && typeof v !== 'string') {
        return (
          <div className="size-full">
            <p
              className="min-xxl:text-lg specs-normal-node-text text-base font-bold text-[#080A0F] md:text-sm"
              dangerouslySetInnerHTML={{
                __html: v?.text || '',
              }}
            />
            <Picture
              source={v?.imgUrl}
              className="l:max-h-[174px] l:max-w-[174px] m-auto max-h-[360px] max-w-[360px]"
              imgClassName="w-full h-full object-cover"
            />
          </div>
        )
      }
      return (
        <div className="break-all">
          {isImageUrl(v) ? (
            <Picture
              source={v}
              className="l:max-h-[174px] l:max-w-[174px] m-auto max-h-[360px] max-w-[360px]"
              imgClassName="w-full h-full object-cover"
            />
          ) : (
            <p
              className="min-xxl:text-lg specs-normal-node-text text-base font-bold text-[#080A0F] md:text-sm"
              dangerouslySetInnerHTML={{
                __html: v || '',
              }}
            />
          )}
        </div>
      )
    }

    const productNode = (data: any) => {
      return data.map((st: any, idx: number) => {
        const isFirstParameter = idx === 0
        return (
          <div
            key={st}
            className={`l-tablet:flex l-tablet:flex-col box-border grid w-full cursor-pointer grid-cols-3 overflow-hidden border-t border-t-[#E4E5E6] ${isFirstParameter ? '' : ''} specs-product-node-wrap`}
          >
            <Heading
              as="h3"
              html={st || ''}
              className="tablet:py-6 l-tablet:text-[#86868C] desktop:p-6 lg-desktop:text-lg specs-product-node-title mr-4 flex-1 pt-4 text-base font-bold md:text-sm"
            />
            <div
              className={`l-tablet:w-full l-tablet:gap-4 laptop:gap-8 desktop:gap-12 desktop:p-6 lg-desktop:gap-16 grid-cols-${isShowMax ? 3 : 2} specs-product-node-body col-span-2 grid flex-1 py-6`}
            >
              {Object.keys(active)?.map?.((item: string) => {
                const currentData = active?.[item]
                const currentMenus = RightMenu?.menus?.find?.(
                  (item: any) => item?.handle === currentData?.handle
                )?.subTitle
                return normalNode(currentMenus?.[st])
              })}
            </div>
          </div>
        )
      })
    }

    // 左侧标题菜单
    const childrenNode = (node: any) => {
      if (!active) return
      const { subTitle, isProduct } = node
      // 如果是产品
      if (isProduct) {
        return (
          <>
            <div
              className={`l-tablet:flex l-tablet:flex-col specs-content-item-product box-border grid w-full cursor-pointer grid-cols-3 border-t-0 border-t-[#E4E5E6]`}
            >
              {node?.title && (
                <Heading as="h3" html={node?.title || ''} className="l:hidden specs-item-product-title" />
              )}
              <div
                className={`l-tablet:w-full l-tablet:gap-4 laptop:gap-8 desktop:gap-12 desktop:px-6 desktop:pb-6 lg-desktop:gap-16 grid-cols-${isShowMax ? 3 : 2} specs-item-product-body col-span-2 grid flex-1 pb-4 pr-0`}
              >
                {Object.keys(active)?.map((item: string, index: number) => {
                  const currentData = active?.[item]
                  const currentProduct = buildData?.products?.find?.(
                    (item: any) => item?.handle === currentData?.handle
                  )
                  return (
                    <div key={item}>{skuNode({ ...currentProduct, item: item, sku: currentData?.sku }, index)}</div>
                  )
                })}
              </div>
            </div>
            {node?.categoryLabel && (
              <Heading
                size={2}
                as="h4"
                html={node?.categoryLabel || ''}
                className="desktop:p-6 specs-item-product-category py-6"
              />
            )}
            {subTitle?.length ? productNode(subTitle) : null}
          </>
        )
      }
      return productNode(subTitle)
    }

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)

    useEffect(() => {
      if (!buildData?.products?.length) return
      const skus = DefaultSelectMenu?.sku?.split?.(',') || []
      const limitedSkus = isMobile && skus?.length > 2 ? skus?.slice(0, 2) : skus
      let currentActive = {}
      limitedSkus?.forEach?.((sku: string, index: number) => {
        const findData = buildData?.products?.find?.((item: any) => {
          const findSku = item?.variants?.find?.((v: any) => v?.sku === sku)
          if (findSku) return true
        })
        if (findData) {
          currentActive = {
            ...currentActive,
            [index]: {
              sku: sku,
              name: findData?.name || findData?.title,
              handle: findData?.handle,
            },
          }
        }
      })
      const currentMenu = buildData?.products?.filter((item: any) => {
        return RightMenu?.menus?.find((params: any) => params?.handle === item?.handle)
      })
      setActive(currentActive)
      setMenuList(currentMenu)
      setIsShowMax(skus?.length > 2 && !isMobile)
    }, [buildData, isMobile])

    useEffect(() => {
      if (isMobile) {
        const currentActive = Object.keys(active || {})
        if (currentActive?.length > 2) {
          const newActive = currentActive?.slice?.(0, 2)
          let newShowActive = {}
          newActive?.forEach?.((item: string) => {
            newShowActive = { ...newShowActive, [item]: active?.[item] }
          })
          setIsShowMax(false)
          setActive(newShowActive)
        }
      }
    }, [isMobile])

    return (
      <div {...rest} className={cn('specs-wrapper w-full overflow-hidden bg-[#F5F5F7]', className)} ref={innerRef}>
        {LeftMenu?.data ? (
          <div className="specs-wrapper-content box-border w-full overflow-hidden">
            {LeftMenu?.data?.map?.((item: any, index: number) => {
              return (
                <div
                  key={index}
                  className="specs-wrapper-content-item w-full overflow-hidden pt-10 [&:first-child]:pt-0"
                >
                  {childrenNode(item)}
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
    )
  }
)

Specs.displayName = 'Specs'

export default withLayout(Specs)
